/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale off -->*/
/*<!-- vale on -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1746057600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu May 01 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "sqlserver-destination",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Connect to SQL Server",
          "depth": 0,
          "id": "connect-to-sql-server"
        }, {
          "value": "SSH tunneling",
          "depth": 1,
          "id": "ssh-tunneling"
        }, {
          "value": "Azure firewall setup",
          "depth": 1,
          "id": "azure-firewall-setup"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      h3: "h3",
      p: "p",
      a: "a",
      ol: "ol",
      li: "li",
      strong: "strong",
      img: "img",
      h4: "h4",
      ul: "ul",
      code: "code",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {PylonLink, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "connect-to-sql-server",
        children: "Connect to SQL Server"
      }), "\n", _jsx(WhitelistDetails, {
        target: "SQL Server instance",
        privatelink: {
          supportedIntegrations: ["aws-privatelink", "gcp-psc"]
        }
      }), "\n", _jsx(_components.h3, {
        id: "ssh-tunneling",
        children: "SSH tunneling"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can connect directly to SQL Server over the public internet or via an SSH tunnel. Since data is encrypted in transit via TLS, a direct connection is suitable for most use cases. You may need to set up a tunnel if your SQL Server instance is on a private network or virtual private cloud (VPC)."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports both standard and reverse SSH tunnels. To learn more about SSH tunneling, refer to Hightouch's ", _jsx(_components.a, {
          href: "/security/ssh-tunneling",
          children: "tunneling documentation"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The ", _jsx(_components.a, {
            href: "#connection-string",
            children: "connection string"
          }), " method doesn't currently support SSH tunneling. Please ", _jsx(PylonLink, {
            pylonText: "Hello, I would like to submit a Feature Request for the SQL Server destination: SSH tunneling with the connection string method.",
            linkText: "reach out to us"
          }), " ", " if you're interested in this feature."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "azure-firewall-setup",
        children: "Azure firewall setup"
      }), "\n", _jsx(_components.p, {
        children: "By default, Azure protects your SQL Server from any external IP address from connecting to your SQL Server. Follow these steps to add Hightouch's IP addresses to the firewall:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Log into your ", _jsx(_components.a, {
            href: "https://portal.azure.com/#home",
            children: "Azure Console Dashboard"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "SQL databases"
          }), " under ", _jsx(_components.strong, {
            children: "Services"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the SQL database you want to update."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Set server firewall"
          }), " .\n", _jsx(_components.img, {
            src: "sources/source-sqlserver-firewall.png",
            alt: "Azure Console Dashboard with settings"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Public network access"
          }), ", select ", _jsx(_components.strong, {
            children: "Selected networks"
          }), ".\n", _jsx(_components.img, {
            src: "sources/source-sqlserver-firewall-setup-1.png",
            alt: "Azure Console Dashboard with settings"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Scroll to ", _jsx(_components.strong, {
            children: "Firewall rules"
          }), " and click ", _jsx(_components.strong, {
            children: "Add a firewall rule"
          }), ".\n", _jsx(_components.img, {
            src: "sources/source-sqlserver-firewall-setup-2.png",
            alt: "Azure Console Dashboard with settings"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Allowlist the ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/security/networking#ip-addresses",
            children: "Hightouch IP addresses"
          }), " for your region."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click the ", _jsx(_components.strong, {
            children: "Save"
          }), " button on the bottom left."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "required-permissions",
        children: "Required permissions"
      }), "\n", _jsx(_components.p, {
        children: "Both setup options require:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Server name (IP address or Azure hostname)"
        }), "\n", _jsx(_components.li, {
          children: "Port number (1433 by default)"
        }), "\n", _jsx(_components.li, {
          children: "Database name"
        }), "\n", _jsx(_components.li, {
          children: "SQL Server Username"
        }), "\n", _jsx(_components.li, {
          children: "SQL Server Password"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Your credentials must be able to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Add/update/delete rows in your sync's table."
        }), "\n", _jsxs(_components.li, {
          children: ["View these tables:\n(*These are used for gathering metadata to set up the sync.)", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "INFORMATION_SCHEMA.COLUMNS"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "INFORMATION_SCHEMA.TABLE_CONSTRAINTS"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "INFORMATION_SCHEMA.CONSTRAINT_COLUMN_USAGE"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "SYS.COLUMNS"
              })
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To ensure your credentials are correct, click ", _jsx(_components.strong, {
          children: "Test connection"
        }), ". This will confirm\nif Hightouch is able to properly connect to your database by running a simple ", _jsx(_components.code, {
          children: "SELECT"
        }), " query."]
      }), "\n", _jsx(_components.h4, {
        id: "password-form",
        children: "Password form"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sqlserver-password-form-setup.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h4, {
        id: "connection-string",
        children: "Connection string"
      }), "\n", _jsxs(_components.p, {
        children: ["Connection string must be in this format:\n", _jsx(_components.code, {
          children: "Server=server;Database=database;User Id=username;Password=password;Encrypt=boolean"
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sqlserver-connection-string-setup.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode with the option to delete removed rows."]
      }), "\n", _jsxs(_components.p, {
        children: ["Select the schema and table you want to sync to. A common schema to use is the database's default, ", _jsx(_components.code, {
          children: "dbo"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sqlserver-schema-table-selections.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Records can be matched from your source to your database table using any primary key or unique column. Hightouch only shows columns that are of the ", _jsx(_components.code, {
          children: "PRIMARY KEY"
        }), " or ", _jsx(_components.code, {
          children: "UNIQUE"
        }), " type in the dropdown."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sqlserver-externalIdMapping.png",
          alt: ""
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Note"
        }), ": We do not display ", _jsx(_components.code, {
          children: "IDENTITY"
        }), " columns as those can't be edited or set\nwhen inserting."]
      }), "\n", _jsx(_components.p, {
        children: "If there are no fields in the dropdown, you must add a primary key or unique constraint to your table in SQL Server. Then, click the refresh icon to access the newly created column."
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Select the fields from your source that you want to sync to SQL Server."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sqlserver-mappings.png",
          alt: ""
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Make sure you account for column types and columns that are ", _jsx(_components.strong, {
            children: "non-nullable"
          }), ".\nIf you upsert any row with the wrong column type or set a null value for a\nnon-nullable column, the entire batch with that row (up to 1k rows) will fail."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", "\n", _jsx(_components.h4, {
        id: "cannot-open-server-hightouch-test-request-by-login",
        children: "Cannot open server 'hightouch-test' request by login"
      }), "\n", "\n", _jsxs(_components.p, {
        children: ["If you see the following error message, you need to ", _jsx(_components.a, {
          href: "#azure-firewall-setup",
          children: "update your firewall settings"
        }), " in your Azure Console."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: ["Cannot open server 'hightouch-test' request by login.\nClient with IP address '", _jsx(_components.span, {
            className: "hljs-number",
            children: "54.196"
          }), _jsx(_components.span, {
            className: "hljs-number",
            children: ".30"
          }), _jsx(_components.span, {
            className: "hljs-number",
            children: ".169"
          }), "' is not allowed to access the server.\nTo enable access", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " use the Windows Azure Management Portal or run sp_set_firewall_rule\non the master database to create a firewall rule for this IP address or address range.\nIt may up take up to five minutes for this change to take effect.\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "invalid-time-error",
        children: "\"Invalid time\" error"
      }), "\n", _jsxs(_components.p, {
        children: ["This error occurs when incorrectly formatted data is sent to a ", _jsxs(_components.a, {
          href: "https://learn.microsoft.com/en-us/sql/t-sql/data-types/time-transact-sql?view=sql-server-ver16",
          children: ["SQL Server ", _jsx(_components.code, {
            children: "time"
          }), " field"]
        }), ".\nTo resolve this issue, you need to reformat your model column data. For example, if your model column contains values formatted like ", _jsx(_components.code, {
          children: "hh:mm:ss"
        }), ", you need to use the ", _jsx(_components.a, {
          href: "/syncs/mapping-data#template-mapping",
          children: "template mapper"
        }), " to change the data's format to:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: [_jsx(_components.span, {
            className: "hljs-number",
            children: "1970"
          }), "-", _jsx(_components.span, {
            className: "hljs-number",
            children: "01"
          }), "-", _jsx(_components.span, {
            className: "hljs-number",
            children: "01"
          }), "T{{ model", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".row"
          }), _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'column_name'"
            }), "]"]
          }), " }}Z\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Be sure to replace ", _jsx(_components.code, {
          children: "column_name"
        }), " with the name of your time column."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sqlserver-template-mapping.png",
          alt: "Template mapping in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["SQL Server accepts these types of values and converts them to the ", _jsx(_components.code, {
          children: "hh:mm:ss.nnnnnnn"
        }), " format SQL Server expects."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
